import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronDown, FiChevronUp } = FiIcons;

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "Can I switch plans after purchasing?",
      answer: "Yes! You can upgrade your plan at any time with pro-rated pricing. If you start with Monthly and want to upgrade to Yearly or Lifetime, you'll only pay the difference. Downgrades are processed with credits for future billing cycles."
    },
    {
      question: "Is there a free trial available?",
      answer: "No free trial is available during our Financial Year End offer as the pricing is already heavily discounted (up to 90% off). However, we offer a 7-day money-back guarantee with no questions asked, so you can try My Webinar risk-free."
    },
    {
      question: "What kind of support do you offer?",
      answer: "We provide 24/7 WhatsApp and email support for all customers. Yearly and Lifetime members get priority support plus a personal onboarding call to help you get started quickly and maximize your results."
    },
    {
      question: "Do you provide onboarding and training?",
      answer: "Absolutely! All customers get access to our quick onboarding playbook and ready-to-use templates. Yearly and Lifetime members also receive a personal onboarding call with our team to customize the platform for their specific needs."
    },
    {
      question: "Are there any hidden fees or limitations?",
      answer: "No hidden fees whatsoever! All plans include unlimited webinars, unlimited attendees, and all core features. The only difference between plans is the billing cycle and level of support. What you see is what you get."
    },
    {
      question: "How does the 7-day money-back guarantee work?",
      answer: "If you're not completely satisfied within 7 days of purchase, simply contact our support team and we'll process a full refund. No questions asked, no lengthy procedures. We're confident you'll love My Webinar."
    },
    {
      question: "Can I use my own domain and branding?",
      answer: "Yes! You can use your own domain and branding. My Webinar allows full customization of your registration pages, thank-you pages, and webinar rooms with your own branding, colors, and logos. You can also connect your own domain by setting up a CNAME on your domain platform, making the entire webinar experience truly yours."
    },
    {
      question: "When does the Financial Year End offer expire?",
      answer: "The Financial Year End offer is valid until 31st March 2026 at 11:59 PM. This is a hard deadline aligned with the end of the Indian financial year. After this date, prices will return to normal. We strongly recommend securing your plan before the deadline."
    }
  ];

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 gradient-bg">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="font-serif text-4xl md:text-5xl font-bold mb-6 text-gray-900">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="text-xl text-gray-600">
            Everything you need to know about My Webinar and our Financial Year End offer
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <motion.div
              key={index}
              className="bg-white rounded-xl overflow-hidden shadow-md border border-gray-200"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <button
                className="w-full px-6 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                onClick={() => toggleFAQ(index)}
              >
                <h3 className="font-semibold text-lg text-gray-900 pr-4">{faq.question}</h3>
                <SafeIcon
                  icon={openIndex === index ? FiChevronUp : FiChevronDown}
                  className="text-primary-blue flex-shrink-0"
                />
              </button>
              <AnimatePresence>
                {openIndex === index && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 text-gray-700 leading-relaxed">{faq.answer}</div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>

        <motion.div
          className="text-center mt-16"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="bg-white border border-blue-200 rounded-2xl p-8 shadow-lg">
            <h3 className="font-serif text-2xl font-bold mb-4 text-gray-900">Still have questions?</h3>
            <p className="text-gray-700 mb-6">Our support team is here to help you 24/7</p>
            <motion.a
              href="https://wa.me/919940890569?text=Hi,%20I%20need%20help%20with%20My%20Webinar%20Financial%20Year%20End%20offer"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-xl font-bold text-lg transition-all hover-glow inline-block"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              WhatsApp Support
            </motion.a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQ;